import React, { useEffect, useState } from 'react';
import { dropdownMenuStyle, menuItemStyle } from '../utils/dropdownStyles';
import { openExplorer, getLastViewedDeployment } from '../utils/explorerLaunch';
import { openChannels, getChannelsBase } from '../utils/channelsLaunch';

export function CommandBar({
    // State
    loading,
    showUsersMenu, setShowUsersMenu,
    // Survey selection
    selectedDeploymentId,
    deployments,
    // User selection
    selectedUserIds,
    selectedUsersData,
    // System status (new)
    aggregateStatus,
    setShowStatusModal,
    // Actions
    refresh,
    logout,
    openDesigner,
    onLinkSelectedUsers,
    onRevokeSelectedUsers,
    onUnstageSelectedUsers,
    // Send modal
    setShowSendModal,
    // User management callbacks
    onEditUser,
    onAddUser,
    onManageTags,
    onAssignTags,
    onImportCsv,
    onDeleteUsers,
    onBrowsePopulation,
    onShowAccount,
    onShowDeploymentRecipients,
    onShowDashboard,
    // Send-monitor surface (from useStagingArea via App.js)
    onShowSendMonitor,
    sendInProgress,
    hasSendResult,
    userName,
    userId,
}) {
    // The Channels button stays greyed until sysconfig has an address. This
    // lets deployment publish the service independently; adding or changing
    // `services.channels` takes effect after Console reloads.
    const [channelsReady, setChannelsReady] = useState(false);
    useEffect(() => {
        let cancelled = false;
        getChannelsBase()
            .then(() => { if (!cancelled) setChannelsReady(true); })
            .catch(() => { if (!cancelled) setChannelsReady(false); });
        return () => { cancelled = true; };
    }, []);

    const aggIcon = aggregateStatus === 'green' ? '✓' : aggregateStatus === 'yellow' ? '⚠' : aggregateStatus === 'red' ? '✕' : '?';
    const aggColor = aggregateStatus === 'green' ? '#22c55e' : aggregateStatus === 'yellow' ? '#f59e0b' : aggregateStatus === 'red' ? '#ef4444' : '#9ca3af';

    const toolbarButtonStyle = (disabled = false, tinted = false) => ({
        background: disabled ? '#F5F6F7' : (tinted ? '#E8F0FE' : '#fff'),
        color: disabled ? '#BDC1C6' : (tinted ? '#1967D2' : '#3C4043'),
        border: '1px solid #DADCE0',
        borderRadius: 4,
        padding: '6px 14px',
        fontSize: 13,
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap',
    });

    const toolbarHoverStyle = (tinted = false) => ({
        background: tinted ? '#D2E3FC' : '#F8F9FA',
        borderColor: tinted ? '#AECBFA' : '#BDC1C6',
    });

    const resetToolbarButton = (target, disabled = false, tinted = false) => {
        if (!disabled) Object.assign(target.style, toolbarButtonStyle(disabled, tinted));
    };

    return (
        <div style={{ gridColumn: '1 / 6', gridRow: '1 / 2', display: 'flex', gap: 16, alignItems: 'center', padding: 8, background: '#1e3a5f', border: '1px solid #152d4a', borderRadius: 6 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                    onClick={() => { if (onShowDashboard) onShowDashboard(); }}
                    style={toolbarButtonStyle(false)}
                    onMouseEnter={(e) => Object.assign(e.target.style, toolbarHoverStyle(false))}
                    onMouseLeave={(e) => resetToolbarButton(e.target, false)}
                >
                    Dashboard
                </button>

                <button
                    onClick={refresh}
                    disabled={loading}
                    style={toolbarButtonStyle(loading)}
                    onMouseEnter={(e) => !loading && Object.assign(e.target.style, toolbarHoverStyle(false))}
                    onMouseLeave={(e) => resetToolbarButton(e.target, loading)}
                >
                    Refresh
                </button>

                {/* Recipients Dropdown */}
                <div data-dropdown style={{ position: 'relative' }}>
                    <button
                        onClick={() => setShowUsersMenu(!showUsersMenu)}
                        style={toolbarButtonStyle(false)}
                        onMouseEnter={(e) => Object.assign(e.target.style, toolbarHoverStyle(false))}
                        onMouseLeave={(e) => resetToolbarButton(e.target, false)}
                    >
                        Recipients ▾
                    </button>
                    {showUsersMenu && (
                        <div style={dropdownMenuStyle}>
                        <button
                            onClick={() => { if (onBrowsePopulation) onBrowsePopulation(); setShowUsersMenu(false); }}
                            style={{ ...menuItemStyle(false), color: '#5B9FFF', fontWeight: 500 }}
                            onMouseEnter={(e) => e.target.style.background = '#F5F6F7'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                            👥 Browse Recipients
                        </button>
                        <button
                            onClick={() => { if (onShowDeploymentRecipients) onShowDeploymentRecipients(); setShowUsersMenu(false); }}
                            disabled={!selectedDeploymentId}
                            style={menuItemStyle(!selectedDeploymentId)}
                            onMouseEnter={(e) => selectedDeploymentId && (e.target.style.background = 'var(--color-bg-base)')}
                            onMouseLeave={(e) => selectedDeploymentId && (e.target.style.background = 'transparent')}
                        >
                            Show Recipients
                        </button>
                        <div style={{ height: 1, background: '#E8EAED', margin: '4px 0' }} />
                        <button
                            onClick={() => { if (onEditUser) onEditUser(); setShowUsersMenu(false); }}
                            disabled={selectedUserIds.size !== 1}
                            style={menuItemStyle(selectedUserIds.size !== 1)}
                            onMouseEnter={(e) => selectedUserIds.size === 1 && (e.target.style.background = 'var(--color-bg-base)')}
                            onMouseLeave={(e) => selectedUserIds.size === 1 && (e.target.style.background = 'transparent')}
                        >
                            Edit Recipient
                        </button>
                        <button
                            onClick={() => { if (onAddUser) onAddUser(); setShowUsersMenu(false); }}
                            style={menuItemStyle(false)}
                            onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-base)'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                            Add Recipient
                        </button>
                        <div style={{ height: 1, background: '#E8EAED', margin: '4px 0' }} />
                        <button
                            onClick={() => { onLinkSelectedUsers(); setShowUsersMenu(false); }}
                            disabled={!(selectedDeploymentId && selectedUserIds.size > 0) || loading}
                            style={menuItemStyle(!(selectedDeploymentId && selectedUserIds.size > 0) || loading)}
                            onMouseEnter={(e) => (selectedDeploymentId && selectedUserIds.size > 0 && !loading) && (e.target.style.background = 'var(--color-bg-base)')}
                            onMouseLeave={(e) => (selectedDeploymentId && selectedUserIds.size > 0 && !loading) && (e.target.style.background = 'transparent')}
                        >
                            Link
                        </button>
                        <button
                            onClick={() => { onRevokeSelectedUsers(); setShowUsersMenu(false); }}
                            disabled={!(selectedDeploymentId && selectedUserIds.size > 0) || loading}
                            style={menuItemStyle(!(selectedDeploymentId && selectedUserIds.size > 0) || loading)}
                            onMouseEnter={(e) => (selectedDeploymentId && selectedUserIds.size > 0 && !loading) && (e.target.style.background = 'var(--color-bg-base)')}
                            onMouseLeave={(e) => (selectedDeploymentId && selectedUserIds.size > 0 && !loading) && (e.target.style.background = 'transparent')}
                        >
                            Unlink
                        </button>
                        <button
                            onClick={() => { if (onUnstageSelectedUsers) onUnstageSelectedUsers(); setShowUsersMenu(false); }}
                            disabled={!(selectedDeploymentId && selectedUserIds.size > 0) || loading}
                            style={menuItemStyle(!(selectedDeploymentId && selectedUserIds.size > 0) || loading)}
                            onMouseEnter={(e) => (selectedDeploymentId && selectedUserIds.size > 0 && !loading) && (e.target.style.background = 'var(--color-bg-base)')}
                            onMouseLeave={(e) => (selectedDeploymentId && selectedUserIds.size > 0 && !loading) && (e.target.style.background = 'transparent')}
                        >
                            Unstage Selected
                        </button>
                        <button
                            disabled
                            style={menuItemStyle(true)}
                            title="Use the Recipient Browser staging area to send. This legacy user-link command has no verified global-recipient identity."
                        >
                            Send (use Staging Area)
                        </button>
                        <button
                            onClick={() => { if (onShowSendMonitor) onShowSendMonitor(); setShowUsersMenu(false); }}
                            disabled={!(sendInProgress || hasSendResult)}
                            style={menuItemStyle(!(sendInProgress || hasSendResult))}
                            onMouseEnter={(e) => (sendInProgress || hasSendResult) && (e.target.style.background = 'var(--color-bg-base)')}
                            onMouseLeave={(e) => (sendInProgress || hasSendResult) && (e.target.style.background = 'transparent')}
                            title={sendInProgress
                                ? 'Open the live send monitor'
                                : hasSendResult
                                    ? 'Re-open the last send report'
                                    : 'No send in progress or recent send to view'}
                        >
                            Send Monitor{sendInProgress ? ' (live)' : ''}
                        </button>
                        <div style={{ height: 1, background: '#E8EAED', margin: '4px 0' }} />
                        <button
                            onClick={() => { if (onManageTags) onManageTags(); setShowUsersMenu(false); }}
                            style={menuItemStyle(false)}
                            onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-base)'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                            Manage Tags
                        </button>
                        <button
                            onClick={() => { if (onAssignTags) onAssignTags(); setShowUsersMenu(false); }}
                            disabled={selectedUserIds.size === 0}
                            style={menuItemStyle(selectedUserIds.size === 0)}
                            onMouseEnter={(e) => selectedUserIds.size > 0 && (e.target.style.background = 'var(--color-bg-base)')}
                            onMouseLeave={(e) => selectedUserIds.size > 0 && (e.target.style.background = 'transparent')}
                        >
                            Assign Tags
                        </button>
                        <button
                            onClick={() => { if (onImportCsv) onImportCsv(); setShowUsersMenu(false); }}
                            style={menuItemStyle(false)}
                            onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-base)'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                            Import CSV
                        </button>
                        <button
                            onClick={() => { if (onBrowsePopulation) onBrowsePopulation(); setShowUsersMenu(false); }}
                            style={menuItemStyle(false)}
                            onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-base)'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                            Archive Recipients
                        </button>
                        <button
                            onClick={() => { if (onDeleteUsers) onDeleteUsers(); setShowUsersMenu(false); }}
                            disabled={selectedUserIds.size === 0}
                            style={{ ...menuItemStyle(selectedUserIds.size === 0), color: selectedUserIds.size > 0 ? '#F44336' : '#9ca3af' }}
                            onMouseEnter={(e) => selectedUserIds.size > 0 && (e.target.style.background = '#FFEBEE')}
                            onMouseLeave={(e) => selectedUserIds.size > 0 && (e.target.style.background = 'transparent')}
                        >
                            Delete
                        </button>
                        </div>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingLeft: 8, paddingRight: 8, borderLeft: '1px solid rgba(255,255,255,0.16)', borderRight: '1px solid rgba(255,255,255,0.16)' }}>
                <button
                    onClick={() => openDesigner('').catch(console.error)}
                    style={toolbarButtonStyle(false, true)}
                    onMouseEnter={(e) => Object.assign(e.target.style, toolbarHoverStyle(true))}
                    onMouseLeave={(e) => resetToolbarButton(e.target, false, true)}
                >
                    Designer
                </button>
                <button
                    onClick={() => openChannels().catch(err => console.error('Open Channels failed:', err))}
                    disabled={!channelsReady}
                    title={channelsReady
                        ? 'Open the Channels screen'
                        : 'The Channels screen address is unavailable.'}
                    style={toolbarButtonStyle(!channelsReady, true)}
                    onMouseEnter={(e) => channelsReady && Object.assign(e.target.style, toolbarHoverStyle(true))}
                    onMouseLeave={(e) => resetToolbarButton(e.target, !channelsReady, true)}
                >
                    Channels
                </button>
                <button
                    onClick={() => {
                        const lastViewed = getLastViewedDeployment(userId);
                        const params = lastViewed
                            ? { ...lastViewed, tab: 'explore', userId }
                            : { tab: 'explore', userId };
                        openExplorer(params).catch(err => console.error('Open Explorer failed:', err));
                    }}
                    style={toolbarButtonStyle(false, true)}
                    onMouseEnter={(e) => Object.assign(e.target.style, toolbarHoverStyle(true))}
                    onMouseLeave={(e) => resetToolbarButton(e.target, false, true)}
                >
                    Explorer
                </button>
            </div>

            <div style={{ flex: 1 }} />

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {userName && (
                    <span style={{ fontSize: 12, color: '#94A3B8', whiteSpace: 'nowrap', marginRight: 4 }}>
                        Logged in: <span style={{ color: '#CBD5E1', fontWeight: 500 }}>{userName}</span>
                    </span>
                )}
                <button
                    onClick={() => { if (onShowAccount) onShowAccount(); }}
                    style={toolbarButtonStyle(false)}
                    onMouseEnter={(e) => Object.assign(e.target.style, toolbarHoverStyle(false))}
                    onMouseLeave={(e) => resetToolbarButton(e.target, false)}
                >
                    Account
                </button>
                <span style={{ fontSize: 16, color: aggColor, lineHeight: 1 }}>{aggIcon}</span>
                <button
                    onClick={() => setShowStatusModal(true)}
                    style={toolbarButtonStyle(false)}
                    onMouseEnter={(e) => Object.assign(e.target.style, toolbarHoverStyle(false))}
                    onMouseLeave={(e) => resetToolbarButton(e.target, false)}
                >Status</button>
                <button
                    onClick={logout}
                    style={toolbarButtonStyle(false)}
                    onMouseEnter={(e) => Object.assign(e.target.style, { background: 'rgba(244,67,54,0.15)', color: '#ff6b6b', borderColor: 'rgba(244,67,54,0.4)' })}
                    onMouseLeave={(e) => resetToolbarButton(e.target, false)}
                >
                    Sign Out
                </button>
            </div>
        </div >
    );
}
